import { MutableRefObject, useRef, useState } from 'react'

import {
  FinanceFactSummariesDocument,
  FinanceFactSummariesQueryVariables,
  FinancialWorkspaceValueObjectsInsightTableTypeEnum,
  FinancialWorkspaceValueObjectsPeriodEnum,
  InputMaybe,
  InsightTableItemsDocument,
  InsightTableItemsQuery,
  InsightTableItemsResourceFilterInput,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import {
  InsightTablePeriod,
  PaginatedTableItem,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { usePermissionsData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'

type FinancialWorkspaceChildrenData = NonNullable<
  NonNullable<
    InsightTableItemsQuery['financialWorkspace']['insightTableItemsConnection']['edges']
  >[number]['node']['children']
>[number]
type FinancialWorkspaceRow = NonNullable<
  NonNullable<
    NonNullable<
      InsightTableItemsQuery['financialWorkspace']['insightTableItemsConnection']['edges']
    >[number]['node']['rows']
  >[number]
>

const CUSTOM_INSIGHT_TABLE_TYPE = ['employee_costs']

type ChildrenData = Omit<FinancialWorkspaceChildrenData, 'conceptId'>

type CustomInsightTableType = (typeof CUSTOM_INSIGHT_TABLE_TYPE)[number]

type InsightTableType = FinancialWorkspaceValueObjectsInsightTableTypeEnum | CustomInsightTableType

interface Variables {
  resourceFilter?: Omit<InsightTableItemsResourceFilterInput, 'insightTableType'> & {
    insightTableType: InsightTableType
  }
  after?: string | null
  sortOrder?: {
    field: string
    order: string
  }
  offset?: number
  itemsPerPage?: number
}

const today = moment()
const startOfMonth = today.startOf('month')
const endOfMonth = today.endOf('month')

const DEFAULT_PERIOD = InsightTablePeriod.Monthly
const DEFAULT_RANGE = `${startOfMonth.toDate().toDateString()}..${endOfMonth
  .toDate()
  .toDateString()}`

const formatData = (
  data: FinancialWorkspaceRow[],
  period?: InputMaybe<FinancialWorkspaceValueObjectsPeriodEnum>
) => {
  return data.map((item) => {
    return {
      range: DEFAULT_RANGE,
      label: item.columnLabel,
      period: period ?? DEFAULT_PERIOD,
      totalAmountInCents: item.amountInCents,
      totalAmountWithPrecision: item.amountWithPrecision ?? undefined,
    }
  })
}

const formatChildren = (
  children: ChildrenData[],
  period?: InputMaybe<FinancialWorkspaceValueObjectsPeriodEnum>
) => {
  return children.map((item) => {
    return {
      id: item.label,
      label: item.label,
      data: formatData(item.rows, period),
      categoryLabel: item?.categoryLabel,
      category: item?.category,
    }
  })
}

const isValidInsightTableType = (
  variables: Variables
): variables is Variables & {
  resourceFilter: InsightTableItemsResourceFilterInput & {
    insightTableType: FinancialWorkspaceValueObjectsInsightTableTypeEnum
  }
} => {
  return !CUSTOM_INSIGHT_TABLE_TYPE.includes(String(variables.resourceFilter?.insightTableType))
}

const summaryVariables = (variables: Variables): FinanceFactSummariesQueryVariables => {
  return {
    formattedStartDate: variables.resourceFilter?.formattedStartDate ?? '',
    formattedEndDate: variables.resourceFilter?.formattedEndDate ?? '',
    groupBy: variables.resourceFilter?.groupBy ?? 'lineitem',
    employeeIds: variables.resourceFilter?.employeeIds ?? [],
    financeTypes: ['compensations'],
    itemsPerPage: variables.itemsPerPage ?? 10,
    after: variables.after,
    locationIds: variables.resourceFilter?.locationIds ?? [],
    legalEntityIds: variables.resourceFilter?.legalEntityIds ?? [],
    costCenterIds: variables.resourceFilter?.costCenterIds ?? [],
    managerIds: variables.resourceFilter?.managerIds ?? [],
    conceptIds: variables.resourceFilter?.conceptIds ?? [],
    customCategoryIds: [],
    excludeChildrenData: variables.resourceFilter?.groupBy === 'total' ? true : false,
    costType: variables.resourceFilter?.costType ?? 'normal',
  }
}

interface IEdge {
  label: string
  children?: ChildrenData[] | null
  rows: FinancialWorkspaceRow[]
  id: string
  categoryLabel?: string | null
  category?: string | null
  active?: boolean
  imageUrl?: string | null
}

interface IRebuildItemsProps {
  edges: IEdge[]
  memoItems: MutableRefObject<PaginatedTableItem[]>
  period?: InputMaybe<FinancialWorkspaceValueObjectsPeriodEnum>
}

const useRebuildItems = ({ edges, memoItems, period }: IRebuildItemsProps) => {
  return useUnsafeMemo(() => {
    const items = (): PaginatedTableItem[] => {
      return (
        edges.map((e) => {
          const { label, children, rows, id, categoryLabel, category, imageUrl, active } = e
          const formattedData = formatData(rows)

          return {
            id,
            label,
            active,
            category,
            imageUrl,
            categoryLabel,
            data: formattedData,
            children: children ? formatChildren(children, period) : undefined,
          }
        }) ?? []
      )
    }

    if (edges) {
      const newUniqueItems = items().filter((e) => !memoItems.current.find((i) => i.id === e.id))
      memoItems.current = newUniqueItems.length
        ? [...memoItems.current, ...newUniqueItems]
        : [...memoItems.current]
    }

    return memoItems.current
  }, [edges, period, memoItems])
}

export const useInsightTableItems = (variables: Variables, skip?: boolean) => {
  const {
    permissions: { canSeeCompensations },
  } = usePermissionsData()
  const memoItems = useRef<PaginatedTableItem[]>([])
  const [headers, setHeaders] = useState<string[]>([])

  const skipQuery =
    skip || CUSTOM_INSIGHT_TABLE_TYPE.includes(String(variables.resourceFilter?.insightTableType))

  useEffect(() => {
    memoItems.current = []
    // Using JSON.stringify to avoid unnecessary re-renders when the resource filter changes
  }, [JSON.stringify(variables.resourceFilter)])

  const {
    error: errorInsightTableItems,
    data: dataInsightTableItems,
    loading: loadingInsightTableItems,
    refetch: refetchInsightTableItems,
  } = useQuery(InsightTableItemsDocument, {
    variables: isValidInsightTableType(variables) ? variables : undefined,
    skip: canSeeCompensations ? true : skipQuery,
    fetchPolicy: 'no-cache',
  })

  const {
    error: errorSummary,
    data: dataSummary,
    loading: loadingSummary,
    refetch: refetchSummary,
  } = useQuery(FinanceFactSummariesDocument, {
    variables: summaryVariables(variables),
    skip: canSeeCompensations ? skipQuery : true,
    fetchPolicy: 'no-cache',
  })

  const connection = canSeeCompensations
    ? dataSummary?.financialWorkspace.financeFactSummariesConnection
    : dataInsightTableItems?.financialWorkspace.insightTableItemsConnection

  const { edges, pageInfo, totalCount } = connection ?? {}

  const showPagination = edges && edges?.length > 0

  useUnsafeMemo(() => {
    // If headers are already set or there are no edges, return
    if (headers.length > 0 || !connection?.edges) return

    const rows = connection.edges[0]?.node?.rows
    setHeaders(rows?.map((row) => row.columnLabel) ?? [])
  }, [connection?.edges, headers.length])

  const newItems = useRebuildItems({
    edges: edges?.map((e) => e.node) || [],
    memoItems,
    period: variables.resourceFilter?.period,
  })

  return {
    error: errorInsightTableItems || errorSummary,
    loading: loadingInsightTableItems || loadingSummary,
    showPagination,
    totalCount: totalCount ?? 0,
    headers,
    items: newItems,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    endCursor: pageInfo?.endCursor,
    refetch: refetchInsightTableItems || refetchSummary,
  }
}
